import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export type StatTileProps = {
  /** Small caps caption above the number. Already translated by the caller. */
  label: string;
  /** The one number the tile exists for, pre-formatted. */
  value: string;
  /**
   * Emphasise this tile's number — the figure the page was opened for.
   *
   * RL-368: the three copies this component replaces had the branch inverted.
   * `highlight` rendered the number WITHOUT `text-primary` and gave it to every
   * other tile instead, so on turnover, weight coverage and shift detail the one
   * number a manager came for (variance, coverage, variance) was the only one
   * de-emphasised. The call sites always marked the right tile; only the
   * rendering disagreed, so fixing it here fixes all three without touching
   * which tile carries the flag.
   */
  highlight?: boolean;
  /** Optional supporting detail under the number — counts, a breakdown. */
  hint?: ReactNode;
  /** When set, the whole tile is a link into the page that holds the detail. */
  to?: string;
  testId?: string;
  className?: string;
};

export function StatTile({ label, value, highlight, hint, to, testId, className }: StatTileProps) {
  const body = (
    <>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('mt-1 text-xl font-semibold tabular-nums', highlight && 'text-primary')}>
        {value}
      </p>
      {hint !== undefined && hint !== null && (
        <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
      )}
    </>
  );

  const base = 'rounded-card border border-border bg-card p-4';

  if (to !== undefined) {
    return (
      <Link
        to={to}
        data-testid={testId}
        className={cn(base, 'block transition-colors hover:border-foreground/40', className)}
      >
        {body}
      </Link>
    );
  }

  return (
    <div data-testid={testId} className={cn(base, className)}>
      {body}
    </div>
  );
}
